import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useGuestSession } from '@/modules/auth'
import { TripService } from '@/modules/trips'
import { useAuth } from '@/modules/auth'
import type { TripWithDetailsResponse } from '@/shared/api'

export function useGuestTrip() {
  const { tripId } = useParams<{ tripId: string }>()
  const { guestSession, isLoading: sessionLoading } = useGuestSession()
  const { getAccessToken } = useAuth()
  const [trip, setTrip] = useState<TripWithDetailsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create trip service instance
  const tripService = useMemo(() => new TripService(getAccessToken), [getAccessToken])

  const loadTrip = async () => {
    if (!tripId || !guestSession || !guestSession.isGuest) {
      setError('Invalid guest session or trip ID')
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      // Prefer guest access token; invite code may still be sent for API compatibility
      const tripData = await tripService.getTripAsGuest(
        tripId,
        guestSession.inviteCode,
        guestSession.guestName,
        guestSession.guestAccessToken
      )
      
      setTrip(tripData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trip details')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!sessionLoading && tripId) {
      if (guestSession) {
        // Use stored guest session with access token
        loadTrip()
      } else {
        // No stored session - show message to join trip
        setError('No guest session found. Join the trip using the share link from the organizer.')
        setIsLoading(false)
      }
    }
  }, [tripId, guestSession, sessionLoading])

  return {
    trip,
    isLoading: isLoading || sessionLoading,
    error,
    loadTrip,
    clearError: () => setError(null),
    guestName: guestSession?.guestName || 'Guest'
  }
}
